import React from "react";
import styled, { css, keyframes } from "styled-components";
import {
  PaintBrush,
  Eraser,
  PaintBucket,
  ArrowUUpLeft,
  ArrowUUpRight,
  SpeakerHigh,
  SpeakerSlash,
} from "@phosphor-icons/react";

import { COMPACT_PALETTE_BREAKPOINT, MOBILE_BREAKPOINT } from "../../constants";
import { usePalette } from "../../contexts/PaletteContext";
import { useTool } from "../../contexts/ToolContext";
import useKey from "../../hooks/useKey";
import useSoundMuted from "../../hooks/useSoundMuted";
import { noFocusOnMouseDown } from "../../utils";
import ColorPalette, { CompactColorPalette } from "../ColorPalette";
import ToolPopover from "../ToolPopover";
import ToolSlider from "../ToolSlider";

const ICON_SIZE = 22;

const Group = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.125rem;

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    flex-direction: row;
  }
`;

const FullPaletteOnly = styled.div`
  display: contents;

  @media (max-width: ${COMPACT_PALETTE_BREAKPOINT}px) {
    display: none;
  }
`;

const CompactPaletteOnly = styled.div`
  display: none;

  @media (max-width: ${COMPACT_PALETTE_BREAKPOINT}px) {
    display: contents;
  }
`;

function DrawingToolbar({
  onUndo,
  onRedo,
  canUndo,
  canRedo,
  disabled = false,
  visible = true,
  showMute = false,
}) {
  const { tool, setTool, penSize, eraserSize, setSize } = useTool();
  const { colorLocked } = usePalette();
  const [muted, setMuted] = useSoundMuted();
  const [openPopover, setOpenPopover] = React.useState(null);

  // Stagger pop-in for icon buttons each time the toolbar reveals. Double-rAF
  // primes "off" before flipping "on" so the animation re-triggers on every
  // false→true cycle, not just first mount.
  const [animateIn, setAnimateIn] = React.useState(false);
  const wasVisibleRef = React.useRef(false);
  React.useEffect(() => {
    if (!visible) {
      setAnimateIn(false);
      wasVisibleRef.current = false;
      return;
    }
    if (wasVisibleRef.current) return;
    wasVisibleRef.current = true;
    setAnimateIn(false);
    let id2 = 0;
    const id1 = requestAnimationFrame(() => {
      id2 = requestAnimationFrame(() => setAnimateIn(true));
    });
    return () => {
      cancelAnimationFrame(id1);
      cancelAnimationFrame(id2);
    };
  }, [visible]);
  // Tracks whether the currently-open popover was opened via hotkey, so that
  // a hotkey open auto-focuses the slider thumb (arrow-key driveable) but a
  // click open keeps focus on the canvas. Cleared on any close.
  const [openedByHotkey, setOpenedByHotkey] = React.useState(false);

  // Single-click selects (and closes any open popover). Double-click and
  // right-click open the tool's size popover — same model as ColorPalette,
  // where single = use, secondary = configure. Single-click on the already-
  // active tool is a no-op.
  //
  // `disabled` short-circuits all of these so the toolbar reads as inert
  // chrome in modes where the canvas isn't accepting input (currently:
  // landing). Hotkeys still register on window-level useKey but no-op
  // because cycleHotkey checks `disabled`.
  const selectTool = (next) => {
    if (disabled) return;
    setTool(next);
    setOpenPopover(null);
    setOpenedByHotkey(false);
  };

  const openPopoverFor = (next) => {
    if (disabled) return;
    setTool(next);
    setOpenPopover(next);
    setOpenedByHotkey(false);
  };

  const onContextMenuFor = (next) => (e) => {
    e.preventDefault();
    openPopoverFor(next);
  };

  // Radix only calls this with `false` (Escape, click-outside) since we drive
  // open via Anchor + caller state, not Trigger.
  const closeIfRequested = (open) => {
    if (!open) {
      setOpenPopover(null);
      setOpenedByHotkey(false);
    }
  };

  // Hotkey cycle: first press selects the tool (closing any popover); a
  // second press of the same hotkey opens that tool's popover (focused);
  // a third press closes it. Tools without a popover (fill) just select.
  // Pressing a different tool's hotkey always switches and closes whatever
  // was open.
  const cycleHotkey = (next, hasPopover) => () => {
    if (disabled) return;
    if (tool !== next) {
      setTool(next);
      setOpenPopover(null);
      setOpenedByHotkey(false);
      return;
    }
    if (!hasPopover) return;
    if (openPopover === next) {
      setOpenPopover(null);
      setOpenedByHotkey(false);
    } else {
      setOpenPopover(next);
      setOpenedByHotkey(true);
    }
  };

  useKey("q", cycleHotkey("pen", true));
  useKey("b", cycleHotkey("pen", true));
  useKey("w", cycleHotkey("fill", false));
  useKey("f", cycleHotkey("fill", false));
  useKey("e", cycleHotkey("eraser", true));

  return (
    <Bar>
      <Group>
        <ToolPopover
          open={openPopover === "pen"}
          onOpenChange={closeIfRequested}
          side="right"
          align="start"
          sideOffset={4}
          autoFocusOnOpen={openedByHotkey && openPopover === "pen"}
          trigger={
            <ToolButton
              type="button"
              $active={tool === "pen"}
              disabled={disabled}
              $animateIn={animateIn}
              $staggerIndex={0}
              onClick={() => selectTool("pen")}
              onDoubleClick={() => openPopoverFor("pen")}
              onContextMenu={onContextMenuFor("pen")}
              title="Pen (q) · double-click or right-click to set size"
              aria-label="Pen"
            >
              <PaintBrush size={ICON_SIZE} weight="fill" />
              <Hint>q</Hint>
            </ToolButton>
          }
        >
          <PopLabel>Pen · {penSize}px</PopLabel>
          <ToolSlider
            min={2}
            max={60}
            value={penSize}
            onValueChange={(v) => setSize(v)}
            ariaLabel="Pen size"
          />
        </ToolPopover>

        <ToolButton
          type="button"
          $active={tool === "fill"}
          disabled={disabled}
          $animateIn={animateIn}
          $staggerIndex={1}
          onClick={() => selectTool("fill")}
          onContextMenu={(e) => e.preventDefault()}
          title="Paint bucket (w)"
          aria-label="Paint bucket"
        >
          <PaintBucket size={ICON_SIZE} weight="fill" />
          <Hint>w</Hint>
        </ToolButton>

        <ToolPopover
          open={openPopover === "eraser"}
          onOpenChange={closeIfRequested}
          side="right"
          align="start"
          sideOffset={4}
          autoFocusOnOpen={openedByHotkey && openPopover === "eraser"}
          trigger={
            <ToolButton
              type="button"
              $active={tool === "eraser"}
              disabled={disabled}
              $animateIn={animateIn}
              $staggerIndex={2}
              onClick={() => selectTool("eraser")}
              onDoubleClick={() => openPopoverFor("eraser")}
              onContextMenu={onContextMenuFor("eraser")}
              title="Eraser (e) · double-click or right-click to set size"
              aria-label="Eraser"
            >
              <Eraser size={ICON_SIZE} weight="fill" />
              <Hint>e</Hint>
            </ToolButton>
          }
        >
          <PopLabel>Eraser · {eraserSize}px</PopLabel>
          <ToolSlider
            min={4}
            max={80}
            value={eraserSize}
            onValueChange={(v) => setSize(v)}
            ariaLabel="Eraser size"
          />
        </ToolPopover>
      </Group>

      {!colorLocked && (
        <Group>
          <FullPaletteOnly>
            <ColorPalette />
          </FullPaletteOnly>
          <CompactPaletteOnly>
            <CompactColorPalette />
          </CompactPaletteOnly>
        </Group>
      )}

      <Group>
        <ToolButton
          type="button"
          onClick={onUndo}
          disabled={disabled || !canUndo}
          $animateIn={animateIn}
          $staggerIndex={3}
          title="Undo (z)"
          aria-label="Undo"
        >
          <ArrowUUpLeft size={ICON_SIZE} />
          <Hint>z</Hint>
        </ToolButton>
        <ToolButton
          type="button"
          onClick={onRedo}
          disabled={disabled || !canRedo}
          $animateIn={animateIn}
          $staggerIndex={4}
          title="Redo (r or shift+z)"
          aria-label="Redo"
        >
          <ArrowUUpRight size={ICON_SIZE} />
          <Hint>r</Hint>
        </ToolButton>
      </Group>

      {showMute && (
        <Group>
          <ToolButton
            type="button"
            onClick={() => setMuted(!muted)}
            $animateIn={animateIn}
            $staggerIndex={5}
            title={muted ? "Unmute" : "Mute"}
            aria-label={muted ? "Unmute" : "Mute"}
            aria-pressed={muted}
          >
            {muted ? (
              <SpeakerSlash size={ICON_SIZE} weight="fill" />
            ) : (
              <SpeakerHigh size={ICON_SIZE} weight="fill" />
            )}
          </ToolButton>
        </Group>
      )}
    </Bar>
  );
}

const Bar = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2rem; // CR nroyalty: make sure that this works on small screens?
  padding: 12px 10px;
  background-color: var(--color-chrome);
  border-right: 1px solid var(--color-chrome-border);
  flex: 0 0 auto;
  overflow: visible;

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    flex-direction: row;
    justify-content: space-around;
    width: 100%;
    gap: 0.5rem;
    padding: 6px 10px 0;
    border-right: none;
    border-top: 1px solid var(--color-chrome-border);
  }
`;

const popIn = keyframes`
  0% {
    opacity: 0;
    transform: scale(0.4) rotate(-14deg);
  }
  60% {
    opacity: 1;
    transform: scale(1.12) rotate(4deg);
  }
  100% {
    opacity: 1;
    transform: scale(1) rotate(0);
  }
`;

const ToolButton = styled.button.attrs({
  onMouseDown: noFocusOnMouseDown,
})`
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 50px;
  height: 50px;
  padding: 0;
  opacity: 0.95;
  border-radius: 6px;
  background: ${(p) =>
    p.$active ? "var(--color-chrome-active)" : "transparent"};
  color: var(--color-chrome-fg);
  border: none;
  cursor: pointer;
  transition:
    background-color 120ms ease-out,
    color 120ms ease-out;

  &:hover:not(:disabled) {
    background: ${(p) =>
      p.$active ? "var(--color-chrome-active)" : "var(--color-chrome-hover)"};
  }

  &:disabled {
    opacity: 0.35;
    cursor: default;
  }

  ${(p) =>
    p.$animateIn &&
    css`
      animation: ${popIn} 380ms ease-out both;
      animation-delay: calc(${p.$staggerIndex ?? 0} * 55ms + 220ms);
    `}

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    width: 40px;
    height: 40px;

    & > svg {
      width: 26px;
      height: 26px;
    }
  }
`;

// Hint inherits color from its ToolButton so it flips light when active.
// Hidden on coarse-pointer devices (phones, tablets) where there's no
// keyboard to press these hotkeys with — tooltip + aria-label still carry
// the info for hover/AT users.
const Hint = styled.span`
  position: absolute;
  bottom: 2px;
  right: 4px;
  font-size: 11px;
  font-weight: 700;
  color: inherit;
  pointer-events: none;
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;

  @media (pointer: coarse) {
    display: none;
  }
`;

const PopLabel = styled.div`
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: var(--color-text-muted);
`;

export default DrawingToolbar;
